/**
 * Financial Store
 * Valtio store for financial metrics state management
 */

import { proxy } from "valtio";
import type {
  FinancialOverview,
  CostBreakdown,
  RevenueTrend,
  SubscriptionBreakdown,
} from "../api/financial-api";
import {
  getSubscriptionTiers,
  getActiveSubscriptions,
  getApiCosts,
  getMonthlySummaries,
  computeFinancialOverview,
  computeCostBreakdown,
  computeSubscriptionBreakdown,
  computeRevenueTrend,
} from "../api/financial-api";
import type {
  SubscriptionTier,
  Subscription,
  ApiCost,
  MonthlySummary,
} from "@/lib/instantdb";

interface FinancialStore {
  // Raw data
  tiers: SubscriptionTier[];
  subscriptions: Subscription[];
  costs: ApiCost[];
  summaries: MonthlySummary[];

  // Computed metrics
  overview: FinancialOverview | null;
  costBreakdown: CostBreakdown | null;
  subscriptionBreakdown: SubscriptionBreakdown | null;
  revenueTrend: RevenueTrend | null;

  // State
  loading: boolean;
  error: string | null;
  timeRange: number; // days
  lastFetched: number | null;
}

export const financialStore = proxy<FinancialStore>({
  // Raw data
  tiers: [],
  subscriptions: [],
  costs: [],
  summaries: [],

  // Computed metrics
  overview: null,
  costBreakdown: null,
  subscriptionBreakdown: null,
  revenueTrend: null,

  // State
  loading: false,
  error: null,
  timeRange: 30,
  lastFetched: null,
});

/**
 * Set the time range for queries
 */
export function setFinancialTimeRange(days: number) {
  financialStore.timeRange = days;
}

/**
 * Fetch all financial data and compute metrics
 */
export async function fetchFinancialData(days?: number) {
  const range = days ?? financialStore.timeRange;
  financialStore.loading = true;
  financialStore.error = null;

  try {
    // Fetch all data in parallel
    const [tiers, subscriptions, costs, summaries] = await Promise.all([
      getSubscriptionTiers(),
      getActiveSubscriptions(),
      getApiCosts(range),
      getMonthlySummaries(12),
    ]);

    // Store raw data
    financialStore.tiers = tiers;
    financialStore.subscriptions = subscriptions;
    financialStore.costs = costs;
    financialStore.summaries = summaries;

    // Compute metrics
    financialStore.overview = computeFinancialOverview(
      subscriptions,
      tiers,
      costs,
      range,
    );
    financialStore.costBreakdown = computeCostBreakdown(costs);
    financialStore.subscriptionBreakdown = computeSubscriptionBreakdown(
      subscriptions,
      tiers,
    );
    financialStore.revenueTrend = computeRevenueTrend(summaries);

    financialStore.lastFetched = Date.now();
  } catch (e) {
    financialStore.error =
      e instanceof Error ? e.message : "Failed to fetch financial data";
  } finally {
    financialStore.loading = false;
  }
}

/**
 * Fetch only cost data (lighter update)
 */
export async function fetchCostData(days?: number) {
  const range = days ?? financialStore.timeRange;
  financialStore.loading = true;
  financialStore.error = null;

  try {
    const costs = await getApiCosts(range);
    financialStore.costs = costs;
    financialStore.costBreakdown = computeCostBreakdown(costs);

    // Update overview with new costs
    if (
      financialStore.tiers.length > 0 &&
      financialStore.subscriptions.length > 0
    ) {
      financialStore.overview = computeFinancialOverview(
        financialStore.subscriptions,
        financialStore.tiers,
        costs,
        range,
      );
    }
  } catch (e) {
    financialStore.error =
      e instanceof Error ? e.message : "Failed to fetch cost data";
  } finally {
    financialStore.loading = false;
  }
}

/**
 * Fetch only subscription data (lighter update)
 */
export async function fetchSubscriptionData() {
  financialStore.loading = true;
  financialStore.error = null;

  try {
    const [tiers, subscriptions] = await Promise.all([
      getSubscriptionTiers(),
      getActiveSubscriptions(),
    ]);

    financialStore.tiers = tiers;
    financialStore.subscriptions = subscriptions;
    financialStore.subscriptionBreakdown = computeSubscriptionBreakdown(
      subscriptions,
      tiers,
    );

    // Update overview with new subscription data
    if (financialStore.costs.length > 0) {
      financialStore.overview = computeFinancialOverview(
        subscriptions,
        tiers,
        financialStore.costs,
        financialStore.timeRange,
      );
    }
  } catch (e) {
    financialStore.error =
      e instanceof Error ? e.message : "Failed to fetch subscription data";
  } finally {
    financialStore.loading = false;
  }
}

/**
 * Clear all financial data
 */
export function clearFinancialData() {
  financialStore.tiers = [];
  financialStore.subscriptions = [];
  financialStore.costs = [];
  financialStore.summaries = [];
  financialStore.overview = null;
  financialStore.costBreakdown = null;
  financialStore.subscriptionBreakdown = null;
  financialStore.revenueTrend = null;
  financialStore.error = null;
  financialStore.lastFetched = null;
}

// ============ Selectors ============

/**
 * Check if data needs refresh (stale after 5 minutes)
 */
export function isDataStale(): boolean {
  if (!financialStore.lastFetched) return true;
  const fiveMinutes = 5 * 60 * 1000;
  return Date.now() - financialStore.lastFetched > fiveMinutes;
}

/**
 * Get formatted MRR string
 */
export function getFormattedMrr(): string {
  const mrr = financialStore.overview?.mrr ?? 0;
  return formatCurrency(mrr);
}

/**
 * Get formatted net profit string
 */
export function getFormattedNetProfit(): string {
  const netProfit = financialStore.overview?.netProfit ?? 0;
  return formatCurrency(netProfit);
}

/**
 * Get formatted total costs string
 */
export function getFormattedTotalCosts(): string {
  const costs = financialStore.overview?.totalCosts ?? 0;
  return formatCurrency(costs);
}

/**
 * Format cents to currency string
 */
export function formatCurrency(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/**
 * Format large numbers with abbreviations
 */
export function formatCompactCurrency(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1000000) {
    return `$${(dollars / 1000000).toFixed(1)}M`;
  }
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(1)}K`;
  }
  return formatCurrency(cents);
}
